import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Loader2, Plus, X, ArrowLeftRight } from 'lucide-react';
import {
  DndContext,
  DragOverlay,
  DragEndEvent,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useIntegrationFlow } from '../../../modules/api-integration/hooks/useIntegrationFlow';
import { useFlowExecution } from '../../../modules/api-integration/hooks/useFlowExecution';
import { IntegrationCanvas } from '../../../modules/api-integration/components/IntegrationCanvas';
import { IntegrationNode, getNodeColor } from '../../../modules/api-integration/components/IntegrationNode';
import { ConnectorSidebar } from '../../../modules/api-integration/components/ConnectorSidebar';
import { ConnectorConfigPanel } from '../../../modules/api-integration/components/ConnectorConfigPanel';
import { DataMappingPanel } from '../../../modules/api-integration/components/DataMappingPanel';
import { DestinationNode } from '../../../modules/api-integration/components/DestinationNode';
import { TriggerNodeConfig } from '../../../modules/api-integration/components/TriggerNodeConfig';
import { NodeConfigDrawer } from '../../../modules/api-integration/components/NodeConfigDrawer';
import { ConnectorType, ConnectorConfig, MappingConfig, DestinationConfig, TriggerConfig, IntegrationNodeType, ProductSyncDetail, FlowType, FLOW_TYPE_LABEL_KEYS } from '../../../modules/api-integration/types';
import { SyncProductDetails } from '../../../modules/api-integration/components/SyncProductDetails';
import { getConnectorIcon, TriggerIcon, MappingIcon, DestinationIcon } from '../../../modules/api-integration/components/ConnectorIcons';
import { CONNECTOR_COLORS, TRIGGER_NODE_COLOR, MAPPING_NODE_COLOR, DESTINATION_NODE_COLOR, getConnectorCatalogEntry } from '../../../modules/api-integration/constants/connector-configs';
import { useApiIntegrationApi } from '../../../common/hooks/use-api-integration-api.hook';
import { PageContainer } from '../../../common/components/PageContainer';

const ApiIntegrationPage: React.FC = () => {
  const { t } = useTranslation();
  const flow = useIntegrationFlow();
  const api = useApiIntegrationApi();

  // Execution scoped to active flow's nodes
  const activeFlowNodes = flow.flows.find(f => f.id === flow.activeFlowId)?.nodes ?? [];
  const execution = useFlowExecution(activeFlowNodes, api);

  const [loading, setLoading] = useState(true);
  const [flowEnabledMap, setFlowEnabledMap] = useState<Record<string, boolean>>({});
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [activeDragConnector, setActiveDragConnector] = useState<ConnectorType | null>(null);
  const [needsRecenter, setNeedsRecenter] = useState(false);
  const [configPanelHeight, setConfigPanelHeight] = useState<number | 'auto'>('auto');
  const resizeState = useRef<{ startY: number; startHeight: number } | null>(null);
  const splitColumnRef = useRef<HTMLDivElement | null>(null);
  const configPanelRef = useRef<HTMLDivElement | null>(null);

  // Resize logic for the split divider
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!resizeState.current) return;
      const delta = resizeState.current.startY - e.clientY;
      const columnHeight = splitColumnRef.current
        ? splitColumnRef.current.getBoundingClientRect().height
        : Infinity;
      const maxHeight = columnHeight - 200;
      const newHeight = Math.min(maxHeight, Math.max(100, resizeState.current.startHeight + delta));
      setConfigPanelHeight(newHeight);
    };
    const handleMouseUp = () => {
      if (!resizeState.current) return;
      resizeState.current = null;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const currentHeight = configPanelHeight === 'auto'
      ? configPanelRef.current?.getBoundingClientRect().height ?? 320
      : configPanelHeight;
    resizeState.current = { startY: e.clientY, startHeight: currentHeight };
    document.body.style.cursor = 'row-resize';
    document.body.style.userSelect = 'none';
  }, [configPanelHeight]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  // Derive selected node from all nodes
  const selectedNode = flow.nodes.find(n => n.id === selectedNodeId) ?? null;

  // When selecting a node, also set its flow as active
  const handleNodeClick = useCallback((nodeId: string, _nodeType: IntegrationNodeType) => {
    setSelectedNodeId(prev => {
      if (prev === nodeId) return null;
      setConfigPanelHeight('auto');
      return nodeId;
    });
    // Cancel the auto-reset timer so execution results stay visible while inspecting
    execution.cancelReset();
    // Set the clicked node's flow as active
    const nodeFlow = flow.flows.find(f => f.nodes.some(n => n.id === nodeId));
    if (nodeFlow) {
      flow.setActiveFlowId(nodeFlow.id);
    }
  }, [flow, execution]);

  // Load integration on mount
  useEffect(() => {
    const load = async () => {
      try {
        const data = await api.getIntegration();
        if (data) {
          // Load per-flow enabled state
          const enabledMap: Record<string, boolean> = {};
          if (Array.isArray(data.flows)) {
            for (const f of data.flows) {
              enabledMap[f.id] = f.isEnabled ?? data.isEnabled ?? false;
            }
          }
          setFlowEnabledMap(enabledMap);
          flow.loadFromBackend(data);
        } else {
          flow.loadFromBackend(null);
        }
      } catch {
        flow.loadFromBackend(null);
      } finally {
        setLoading(false);
        setNeedsRecenter(true);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-recenter after loading nodes
  useEffect(() => {
    if (needsRecenter && flow.nodes.length > 0) {
      const timer = setTimeout(() => {
        const canvasEl = document.querySelector('[data-testid="integration-canvas"]');
        if (canvasEl) {
          const rect = canvasEl.getBoundingClientRect();
          flow.recenter(rect.width, rect.height);
        }
        setNeedsRecenter(false);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [needsRecenter, flow.nodes.length, flow.recenter]);

  const saveFlow = useCallback(async (overrides?: Record<string, any>) => {
    const payload = flow.toBackendPayload();
    if (!payload) return;
    // Inject per-flow isEnabled into each flow payload
    const enrichedFlows = payload.flows.map((f: any) => ({
      ...f,
      isEnabled: flowEnabledMap[f.id] ?? false,
    }));
    try {
      await api.saveIntegration({ ...payload, flows: enrichedFlows, isEnabled: Object.values(flowEnabledMap).some(Boolean), ...overrides });
    } catch {
      // silently fail
    }
  }, [flow, api, flowEnabledMap]);

  const handleFlowEnabledToggle = useCallback(async (flowId: string, checked: boolean) => {
    setFlowEnabledMap(prev => ({ ...prev, [flowId]: checked }));
    // Save immediately
    const payload = flow.toBackendPayload();
    if (payload) {
      const updatedMap = { ...flowEnabledMap, [flowId]: checked };
      const enrichedFlows = payload.flows.map((f: any) => ({
        ...f,
        isEnabled: updatedMap[f.id] ?? false,
      }));
      try {
        await api.saveIntegration({ ...payload, flows: enrichedFlows, isEnabled: Object.values(updatedMap).some(Boolean) });
      } catch {
        setFlowEnabledMap(prev => ({ ...prev, [flowId]: !checked }));
      }
    }
  }, [flow, api, flowEnabledMap]);

  const handleDeleteFlow = useCallback(async (nodeId: string) => {
    setSelectedNodeId(null);
    flow.deleteNode(nodeId);
    if (flow.flows.length <= 1) {
      // Last flow deleted — delete integration
      try {
        await api.deleteIntegration();
      } catch {
        // ignore
      }
    } else {
      // Multiple flows — save the remaining ones
      saveFlow();
    }
  }, [flow, api, saveFlow]);

  const handleResetFlow = useCallback(async () => {
    setSelectedNodeId(null);
    flow.resetFlow();
    try {
      await api.deleteIntegration();
    } catch {
      // ignore
    }
  }, [flow, api]);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const connectorType = event.active.data.current?.connectorType as ConnectorType;
    if (connectorType) {
      setActiveDragConnector(connectorType);
    }
  }, []);

  /**
   * Put a connector on the board.
   *
   * Shared by the click and the drop because they were always the same act:
   * the drop discards where you let go and centres the flow on the canvas, so
   * the gesture only ever carried which connector you wanted. Dragging stayed
   * the only way to say it, which is why clicking one appeared to do nothing.
   */
  const placeConnector = useCallback((connectorType: ConnectorType) => {
    const canvasEl = document.querySelector('[data-testid="integration-canvas"]');
    if (!canvasEl) return;

    const canvasRect = canvasEl.getBoundingClientRect();
    const flowWidth = 860;
    const flowHeight = 90;
    const x = (canvasRect.width / flow.zoom - flowWidth) / 2;
    const y = (canvasRect.height / flow.zoom - flowHeight) / 2;

    if (flow.flows.length === 0) {
      // First flow — use addSourceNode behavior (centered, defaults to create_and_update)
      flow.setPan(0, 0);
      flow.setZoom(1);
      flow.addSourceNode(connectorType, x, y);
    } else if (flow.availableFlowTypes.length > 0) {
      // Second flow — same connector, pick first available flow type
      flow.addFlow(flow.availableFlowTypes[0], connectorType, x, y);
      setNeedsRecenter(true);
    }
  }, [flow]);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    setActiveDragConnector(null);
    const { active, over } = event;

    if (!over || over.id !== 'integration-canvas') return;

    const connectorType = active.data.current?.connectorType as ConnectorType;
    if (connectorType) placeConnector(connectorType);
  }, [placeConnector]);

  // Get the icon component for a node. The connector icons are still MUI
  // SvgIcons owned by the api-integration module, so they are sized through
  // `style` — an inline font-size is what an SvgIcon scales from.
  const getNodeIconComponent = (node: typeof selectedNode) => {
    if (!node) return null;
    if (node.type === 'trigger') return <TriggerIcon style={{ color: TRIGGER_NODE_COLOR, fontSize: 16 }} />;
    if (node.type === 'source' && node.connectorType) {
      const Icon = getConnectorIcon(node.connectorType);
      return <Icon style={{ color: CONNECTOR_COLORS[node.connectorType], fontSize: 16 }} />;
    }
    if (node.type === 'mapping') return <MappingIcon style={{ color: MAPPING_NODE_COLOR, fontSize: 16 }} />;
    return <DestinationIcon style={{ color: DESTINATION_NODE_COLOR, fontSize: 16 }} />;
  };

  // Render execution results panel for the selected node
  const renderExecutionResults = () => {
    if (!selectedNode || selectedNode.type === 'mapping') return null;
    const nodeResult = execution.executionState.nodeResults[selectedNode.id];
    const nodeStatus = execution.executionState.nodeStatuses[selectedNode.id];
    if (!nodeResult || (nodeStatus !== 'completed' && nodeStatus !== 'failed')) return null;

    const isCompleted = nodeStatus === 'completed';
    return (
      <div
        data-testid="execution-results-panel"
        className={cn(
          'mb-4 rounded-xl border p-3',
          isCompleted ? 'border-success/25 bg-success/5' : 'border-destructive/25 bg-destructive/5',
        )}
      >
        <p
          className={cn(
            'mb-2 text-xs font-medium tracking-wider uppercase',
            isCompleted ? 'text-success' : 'text-destructive',
          )}
        >
          {isCompleted ? t('PAGES.API_INTEGRATION.COMPLETED') : t('PAGES.API_INTEGRATION.FAILED')}
        </p>
        {nodeResult.details?.map((detail, idx) => (
          <div key={idx} className="flex items-center justify-between py-0.5">
            <span className="text-xs text-muted-foreground">{detail.label}</span>
            <span className="font-mono text-xs font-medium text-foreground">{detail.value}</span>
          </div>
        ))}
        {nodeResult.errors && nodeResult.errors.length > 0 && (
          <div className="mt-2">
            {nodeResult.errors.map((error, idx) => (
              <p key={idx} className="font-mono text-[10px] leading-snug text-destructive">
                {error}
              </p>
            ))}
          </div>
        )}
        {selectedNode?.type === 'destination' && nodeResult.productDetails && nodeResult.productDetails.length > 0 && (
          <SyncProductDetails productDetails={nodeResult.productDetails} />
        )}
      </div>
    );
  };

  // Render drawer content based on selected node type
  const renderDrawerContent = () => {
    if (!selectedNode) return null;

    if (selectedNode.type === 'trigger') {
      return (
        <TriggerNodeConfig
          config={selectedNode.config as TriggerConfig}
          onConfigChange={(config) => flow.updateNodeConfig(selectedNode.id, config)}
          onSave={() => saveFlow()}
        />
      );
    }

    if (selectedNode.type === 'source' && flow.sourceNode?.connectorType) {
      return (
        <ConnectorConfigPanel
          connectorType={flow.sourceNode.connectorType}
          config={flow.sourceNode.config as ConnectorConfig}
          onConfigChange={(config) => flow.updateNodeConfig(selectedNode.id, config)}
          onSave={() => saveFlow()}
          onTestConnection={async () => {
            const sourceConfig = flow.sourceNode!.config as ConnectorConfig;
            const { connectorType, ...connectorConfig } = sourceConfig;
            return await api.testConnection({ connectorType, connectorConfig, flowId: flow.activeFlowId });
          }}
        />
      );
    }

    if (selectedNode.type === 'mapping' && flow.sourceNode?.connectorType && flow.mappingNode) {
      return (
        <DataMappingPanel
          connectorType={flow.sourceNode.connectorType}
          config={flow.mappingNode.config as MappingConfig}
          onSave={(config) => {
            flow.updateNodeConfig(flow.mappingNode!.id, config);
            saveFlow();
          }}
          connectorConfig={flow.sourceNode?.config as Record<string, any>}
          onFetchSample={async (connectorType, connectorConfig) => {
            return await api.fetchSampleProduct({ connectorType, connectorConfig, flowId: flow.activeFlowId });
          }}
          onAiMap={api.runAiMapping}
        />
      );
    }

    if (selectedNode.type === 'destination' && flow.destinationNode) {
      const mappingCfg = flow.mappingNode?.config as MappingConfig | undefined;
      const mappedTargetFields = mappingCfg
        ? [...new Set(mappingCfg.mappings.map(m => m.targetField))]
        : [];
      return (
        <DestinationNode
          config={flow.destinationNode.config as DestinationConfig}
          onConfigChange={(config) => flow.updateNodeConfig(selectedNode.id, config)}
          onSave={() => saveFlow()}
          mappedTargetFields={mappedTargetFields}
        />
      );
    }

    return null;
  };

  const renderFlowTabs = () => {
    if (flow.flows.length === 0) return null;

    return (
      <div className="mb-4 flex flex-wrap items-center gap-2.5">
        {flow.flows.map((f) => {
          const isActive = f.id === flow.activeFlowId;
          const sourceNode = f.nodes.find(n => n.type === 'source');
          // The connector's own brand colour identifies the flow; it is data,
          // not chrome, so it stays an inline value rather than a token.
          const connectorColor = sourceNode?.connectorType
            ? CONNECTOR_COLORS[sourceNode.connectorType]
            : undefined;

          return (
            <div
              key={f.id}
              data-testid={`flow-tab-${f.id}`}
              onClick={() => flow.setActiveFlowId(f.id)}
              className={cn(
                'flex cursor-pointer items-center gap-2 rounded-lg border px-4 py-2 transition-colors',
                isActive ? 'bg-background' : 'border-border bg-background hover:bg-muted',
              )}
              style={isActive && connectorColor
                ? { borderColor: connectorColor, backgroundColor: `${connectorColor}10` }
                : undefined}
            >
              {/* Colour dot */}
              <span
                className="size-2.5 shrink-0 rounded-full bg-muted-foreground"
                style={connectorColor ? { backgroundColor: connectorColor } : undefined}
              />

              <span
                className={cn(
                  'text-sm font-medium whitespace-nowrap',
                  isActive ? 'text-foreground' : 'text-muted-foreground',
                )}
              >
                {f.flowType ? t(FLOW_TYPE_LABEL_KEYS[f.flowType]) : f.name}
              </span>

              {/* Change flow type — only if the other type is available. The
                  label is on the button itself: a tooltip trigger cannot also
                  be a menu trigger. */}
              {isActive && flow.availableFlowTypes.length > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon-xs"
                      aria-label={t('PAGES.API_INTEGRATION.CHANGE_FLOW_TYPE', 'Change flow type')}
                      className="text-muted-foreground"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <ArrowLeftRight className="size-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="min-w-[200px]">
                    {flow.availableFlowTypes.map((ft) => (
                      <DropdownMenuItem
                        key={ft}
                        onClick={() => {
                          flow.changeFlowType(f.id, ft);
                          saveFlow();
                        }}
                      >
                        {t(FLOW_TYPE_LABEL_KEYS[ft])}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}

              {/* Remove flow button */}
              {isActive && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon-xs"
                      aria-label={t('PAGES.API_INTEGRATION.REMOVE_FLOW')}
                      className="text-muted-foreground hover:text-destructive"
                      onClick={(e) => {
                        e.stopPropagation();
                        const nodeToDelete = f.nodes.find(n => n.type === 'source');
                        if (nodeToDelete) handleDeleteFlow(nodeToDelete.id);
                      }}
                    >
                      <X className="size-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{t('PAGES.API_INTEGRATION.REMOVE_FLOW')}</TooltipContent>
                </Tooltip>
              )}
            </div>
          );
        })}

        {/* Add Flow button — only if there's a remaining flow type to add */}
        {flow.availableFlowTypes.length > 0 && flow.currentConnectorType && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                data-testid="add-flow-button"
                variant="ghost"
                className="h-auto rounded-lg border border-dashed border-border px-2.5 py-1.5 text-xs text-muted-foreground"
                onClick={() => {
                  const ft = flow.availableFlowTypes[0];
                  flow.addFlow(ft, flow.currentConnectorType!, 50, 100);
                  setNeedsRecenter(true);
                }}
              >
                <Plus className="size-3.5" />
                {t(FLOW_TYPE_LABEL_KEYS[flow.availableFlowTypes[0]])}
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t(FLOW_TYPE_LABEL_KEYS[flow.availableFlowTypes[0]])}</TooltipContent>
          </Tooltip>
        )}
      </div>
    );
  };

  const renderCanvas = () => {
    // Filter to active flow only
    const activeFlow = flow.flows.find(f => f.id === flow.activeFlowId);
    const activeNodes = activeFlow?.nodes ?? [];
    const activeFlows = activeFlow ? [activeFlow] : [];

    // Check canStart for active flow
    const hasMappings = ((flow.mappingNode?.config as MappingConfig)?.mappings?.length ?? 0) > 0;
    const isSourceConfigured = (() => {
      if (!flow.sourceNode?.connectorType) return false;
      const cfg = flow.sourceNode.config as Record<string, any>;
      const fields = getConnectorCatalogEntry(flow.sourceNode.connectorType).configFields;
      return fields.every(f => cfg[f.key] && String(cfg[f.key]).trim() !== '');
    })();
    const canStart = isSourceConfigured && hasMappings;

    const triggerConfig = flow.triggerNode?.config as TriggerConfig | undefined;
    const currentSyncMode = triggerConfig?.syncMode ?? 'manual';

    return (
      <IntegrationCanvas
        nodes={activeNodes}
        flows={activeFlows}
        zoom={flow.zoom}
        panX={flow.panX}
        panY={flow.panY}
        onZoomChange={flow.setZoom}
        onPanChange={flow.setPan}
        onRecenter={flow.recenter}
        isRunning={execution.isRunning}
        executionStatus={execution.executionState.status}
        onStart={execution.startExecution}
        canStart={canStart}
        nodeStatuses={execution.executionState.nodeStatuses}
        flowEnabled={activeFlow ? (flowEnabledMap[activeFlow.id] ?? false) : false}
        onToggleEnabled={activeFlow ? (checked) => handleFlowEnabledToggle(activeFlow.id, checked) : undefined}
        syncMode={currentSyncMode}
      >
        {activeNodes.map(node => {
          const nodeSourceConfigured = (() => {
            if (!flow.sourceNode?.connectorType) return false;
            const cfg = flow.sourceNode.config as Record<string, any>;
            const fields = getConnectorCatalogEntry(flow.sourceNode.connectorType).configFields;
            return fields.every(f => cfg[f.key] && String(cfg[f.key]).trim() !== '');
          })();
          const nodeHasMappings = ((flow.mappingNode?.config as MappingConfig)?.mappings?.length ?? 0) > 0;

          const isDisabled =
            (node.type === 'mapping' && !nodeSourceConfigured) ||
            (node.type === 'destination' && !nodeHasMappings);
          return (
            <IntegrationNode
              key={node.id}
              node={node}
              zoom={flow.zoom}
              onDrag={flow.updateNodePosition}
              onDelete={handleDeleteFlow}
              onConfigChange={flow.updateNodeConfig}
              onClick={handleNodeClick}
              isSelected={node.id === selectedNodeId}
              disabled={isDisabled}
              executionStatus={execution.executionState.nodeStatuses[node.id] ?? 'idle'}
              executionResult={execution.executionState.nodeResults[node.id]}
            />
          );
        })}
      </IntegrationCanvas>
    );
  };

  const DragOverlayContent = activeDragConnector ? (() => {
    const IconComponent = getConnectorIcon(activeDragConnector);
    const color = CONNECTOR_COLORS[activeDragConnector];
    const name = activeDragConnector.charAt(0).toUpperCase() + activeDragConnector.slice(1).replace('_', ' ');
    return (
      <div
        className="flex w-[180px] items-center gap-3 rounded-xl border bg-background px-4 py-2.5 opacity-90 shadow-sm"
        style={{ borderColor: color }}
      >
        <span
          className="flex size-7 items-center justify-center rounded-full"
          style={{ backgroundColor: `${color}15` }}
        >
          <IconComponent style={{ color, fontSize: 14 }} />
        </span>
        <span className="text-sm font-medium text-foreground">{name}</span>
      </div>
    );
  })() : null;

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="size-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <PageContainer
      requiresFeature="apiIntegration"
      title={t('PAGES.API_INTEGRATION.TITLE')}
      breadcrumbs={[
        { label: t('SIDEBAR.CUSTOMIZATION.TITLE') },
        { label: t('SIDEBAR.CUSTOMIZATION.SYNCHRONIZATION') },
      ]}
    >
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="flex h-[calc(100vh-140px)] flex-col">
        {/* Flow Tabs — lifted out of the canvas column and rendered full-width
            above so the canvas top and the connector sidebar top line up. */}
        {renderFlowTabs()}

        {/* Main Layout */}
        <div className="flex min-h-0 flex-1 items-stretch gap-6">
          {/* Left column: Canvas + Divider + Config Panel */}
          <div ref={splitColumnRef} className="flex min-w-0 flex-1 flex-col">
            {/* Canvas area */}
            <div className="relative min-h-0 flex-1">
              <div className="absolute inset-0 overflow-hidden">
                {renderCanvas()}
              </div>
            </div>

            {/* Resize divider + Config panel (only when a node is selected) */}
            {selectedNode && (
              <React.Fragment>
                {/* Resize Divider */}
                <div
                  onMouseDown={handleResizeStart}
                  className="group/divider flex h-2 shrink-0 cursor-row-resize items-center justify-center border-y bg-background"
                >
                  {/* The grip picks up the selected node's colour on hover, so
                      the variable carries a value Tailwind cannot know about. */}
                  <span
                    className="h-1 w-12 bg-border transition-all group-hover/divider:bg-[var(--grip-color)] group-hover/divider:opacity-50"
                    style={{ ['--grip-color' as any]: getNodeColor(selectedNode) }}
                  />
                </div>

                {/* Config Panel */}
                <div
                  ref={configPanelRef}
                  className={cn('shrink-0', configPanelHeight !== 'auto' && 'overflow-hidden')}
                  style={{ height: configPanelHeight === 'auto' ? 'auto' : configPanelHeight }}
                >
                  <NodeConfigDrawer
                    onClose={() => setSelectedNodeId(null)}
                    title={selectedNode.title?.startsWith('PAGES.') ? t(selectedNode.title) : (selectedNode.title || '')}
                    subtitle={(() => {
                      const sub = selectedNode.subtitle || '';
                      if (!sub.startsWith('PAGES.')) return sub;
                      if (sub.includes('FIELDS_MAPPED')) {
                        const key = sub.substring(0, sub.lastIndexOf(':'));
                        const count = parseInt(sub.substring(sub.lastIndexOf(':') + 1), 10) || 0;
                        return String(t(key, { count } as any));
                      }
                      return String(t(sub));
                    })()}
                    nodeColor={getNodeColor(selectedNode)}
                    icon={getNodeIconComponent(selectedNode) || <DestinationIcon style={{ fontSize: 16 }} />}
                    wide={selectedNode.type === 'mapping'}
                  >
                    <div className="flex gap-0">
                      {/* Left column: config */}
                      <div className="min-w-0 flex-1">
                        {renderDrawerContent()}
                      </div>
                      {/* Right column: execution results */}
                      {renderExecutionResults() && (
                        <div className="ms-4 min-w-0 flex-1 border-s ps-4">
                          {renderExecutionResults()}
                        </div>
                      )}
                    </div>
                  </NodeConfigDrawer>
                </div>
              </React.Fragment>
            )}
          </div>

          {/* Sidebar */}
          <ConnectorSidebar
            onAdd={(connector) => placeConnector(connector.type)}
            currentConnectorType={flow.currentConnectorType}
            hasRoom={flow.availableFlowTypes.length > 0}
          />
        </div>
      </div>

      {/* Drag Overlay */}
      <DragOverlay dropAnimation={null}>
        {DragOverlayContent}
      </DragOverlay>
    </DndContext>
    </PageContainer>
  );
};

export default ApiIntegrationPage;
